import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, watch } from "vue";
import {
  AspectRatios,
  MIN_DURATION,
  MAX_DURATION,
  DEFAULT_ENCODE_SETTINGS,
  MAX_FRAME_RATE,
  MAX_FRAME_COUNT,
  type Settings
} from "../composables/use-encode-settings";
import VideoPlayer from "@/components/tools/VideoPlayer.vue";
import toPrecision from "@/utils/to-precision";
import AppButton from "@/components/AppButton.vue";
import GifSettingsRatios from "./GifSettingsRatios.vue";
import clamp from "@/utils/clamp";
import GifSettingsFrameRate from "./GifSettingsFrameRate.vue";
import VideoTrim from "@/components/tools/VideoTrim.vue";
import TimeField from "@/components/tools/TimeField.vue";
import VideoTimeline from "@/components/tools/VideoTimeline.vue";
import FileUpload from "@/components/tools/FileUpload.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'GifSettings',
  props: {
    settings: null
  },
  emits: ["encode"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: "encode", values: Settings): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  settings: Settings;
}





const FRAME_RATES = [5, 7, 8, 10, 12, 20, 25, 30];
const DEFAULT_VIDEO_DURATION = 90;
const MAX_FILESIZE = 100 * 1024 * 1024; // 100MB

const settings = reactive<Settings>({
  videoUrl: undefined,
  frameRate: 10,
  aspectRatio: AspectRatios.AR16x9,
  startTime: 0,
  endTime: 0
});

const videoElement = ref<HTMLVideoElement>();
const videoDuration = ref<number>(90);
const videoCurrentTime = ref(0);
const uploaded = ref(false);

watch(
  () => props.settings,
  (newSettings: Settings) => {
    Object.assign(settings, newSettings);
  },
  {
    immediate: true
  }
);

const duration = computed(() => {
  return settings.endTime - settings.startTime;
});

const startTimeInputValue = computed({
  get() {
    return settings.startTime;
  },
  set(value: number) {
    const min = 0;
    const max = settings.endTime - MIN_DURATION;

    settings.startTime = clamp(value, min, max);
  }
});

const endTimeInputValue = computed({
  get() {
    return settings.endTime;
  },
  set(value: number) {
    const min = settings.startTime + MIN_DURATION;
    const max = videoDuration.value ?? 0;

    settings.endTime = clamp(value, min, max);
  }
});

const disabled = computed(() => {
  return !settings.videoUrl;
});

const playbackProgress = computed(() => {
  return (videoCurrentTime.value - settings.startTime) / duration.value;
});

const onVideoUpload = (file: File) => {
  settings.videoUrl = URL.createObjectURL(file);
  uploaded.value = true;
};

const onVideoLoaded = (video: HTMLVideoElement) => {
  videoElement.value = video;

  if (uploaded.value) {
    settings.startTime = 0;
    settings.endTime = Math.min(toPrecision(video.duration, 1), MAX_DURATION);
    settings.frameRate = maxFrameRate.value;
  }

  videoDuration.value = toPrecision(video.duration, 1);
  resetPlayback();
};

const stopPlayback = () => {
  resetPlayback();
};

const startPlayback = () => {
  videoElement.value?.play();
};

const maxFrameRate = computed(() => {
  const max = Math.min(MAX_FRAME_RATE, MAX_FRAME_COUNT / duration.value);

  const closestMaxMatch = FRAME_RATES.reduce((prev, curr) => {
    return Math.abs(curr - max) < Math.abs(prev - max) ? curr : prev;
  });

  return closestMaxMatch;
});

const encode = () => {
  if (!disabled.value) {
    emits("encode", {
      ...settings
    });
  }
};

const onVideoTimeupdate = (currentTime: number) => {
  videoCurrentTime.value = currentTime;
};

const resetPlayback = () => {
  if (videoElement.value) {
    videoElement.value.pause();
    videoElement.value.currentTime = settings.startTime;
  }
};

const trimVideo = (newStartTime: number, newEndTime: number) => {
  settings.startTime = newStartTime;
  settings.endTime = newEndTime;
  setFrameRateToMax();
  seekVideoToTime(newStartTime);
};

const setFrameRateToMax = () => {
  settings.frameRate = maxFrameRate.value;
};

const seekVideoToTime = (seconds: number) => {
  if (videoElement.value) {
    videoElement.value.currentTime = seconds;
  }
};

const removeVideo = () => {
  Object.assign(settings, DEFAULT_ENCODE_SETTINGS);

  videoElement.value = undefined;
  videoDuration.value = DEFAULT_VIDEO_DURATION;
  videoCurrentTime.value = 0;
  uploaded.value = false;
};

return { __sfc: true,emits, props, FRAME_RATES, DEFAULT_VIDEO_DURATION, MAX_FILESIZE, settings, videoElement, videoDuration, videoCurrentTime, uploaded, duration, startTimeInputValue, endTimeInputValue, disabled, playbackProgress, onVideoUpload, onVideoLoaded, stopPlayback, startPlayback, maxFrameRate, encode, onVideoTimeupdate, resetPlayback, trimVideo, setFrameRateToMax, seekVideoToTime, removeVideo, AspectRatios, MIN_DURATION, MAX_DURATION, MAX_FRAME_RATE, VideoPlayer, AppButton, GifSettingsRatios, GifSettingsFrameRate, VideoTrim, TimeField, VideoTimeline, FileUpload }
}

})